import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { PackagesService } from '../../services/payments/packages/packages.service';
import { PackagesResponse } from '../../models/payment/packagesResponse';
import { apiResponse } from '../../models/apiResponse';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

@Component({
    selector: 'app-shop',
    standalone: true,
    imports: [CommonModule, InputTextModule, ProgressSpinnerModule],
    templateUrl: './shop.component.html',
    styleUrls: ['./shop.component.scss']
})
export class ShopComponent implements OnInit {
    categories = [
        "All",
        "Undergraduate Admissions",
        "Postgraduate Admissions",
        "Post Admission Services",
        "Housing",
        "Education Loan",
        "Education Counselling",
        "Writing Services"
    ];
    activeCategory = "All";
    packages: PackagesResponse[] = [];
    filteredPackages: PackagesResponse[] = [];
    paginatedPackages: PackagesResponse[] = [];
    loading = true;
    searchQuery: string = "";
    expandedFeatures = new Set<number>();

 
    currentPage: number = 1;
    itemsPerPage: number = 6;
    totalPages: number = 1;

    categoryMap: Record<number, string> = {
        1: 'Undergraduate Admissions',
        2: 'Postgraduate Admissions',
        3: 'Post Admission Services',
        4: 'Housing',
        5: 'Education Loan',
        6: 'Education Counselling',
        7: 'Writing Services'
    };

    packageIcons: Record<string, string> = {
        'Undergraduate Admissions': '🎓',
        'Postgraduate Admissions': '🎓',
        'Post Admission Services': '🤝',
        'Housing': '🏠',
        'Education Loan': '💰',
        'Education Counselling': '💬',
        'Writing Services': '📝',
        'Other': '📦',
        'Default': '📦'
    };

    searchLoading = false;
    showSuggestions = false;
    searchSuggestions: PackagesResponse[] = [];
    private searchTimeout: any;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private packagesService: PackagesService
    ) { }

    ngOnInit() {
        this.route.queryParamMap.subscribe(params => {
            const filter = params.get('category');
            if (filter) this.activeCategory = filter;
            this.loadPackages();
        });
    }

    loadPackages() {
        this.loading = true;
        this.packagesService.getActivePackages().subscribe({
            next: (res: apiResponse<PackagesResponse[]>) => {
                this.packages = res.result || [];
                this.filterPackages();
                this.loading = false;
            },
            error: () => {
                this.loading = false;
            }
        });
    }

    setCategory(cat: string) {
        this.activeCategory = cat;
        this.filterPackages();
    }

    onSearch(event: any) {
        this.searchQuery = event.target.value || '';

        if (this.searchQuery.length < 3) {
            this.showSuggestions = false;
            this.searchSuggestions = [];
            this.filterPackages();
            return;
        }

        this.searchLoading = true;
        this.showSuggestions = true;

        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }

        this.searchTimeout = setTimeout(() => {
            const query = this.searchQuery.toLowerCase().trim();

            this.searchSuggestions = this.packages.filter(p =>
                p.name.toLowerCase().includes(query) ||
                (p.description && p.description.toLowerCase().includes(query))
            );

            this.filterPackages();

            this.searchLoading = false;
        }, 300);
    }

    selectSuggestion(pkg: PackagesResponse) {
        this.searchQuery = pkg.name;
        this.showSuggestions = false;
        this.filterPackages();
    }

    filterPackages() {
        let temp = this.packages;
        if (this.activeCategory !== "All") {
            temp = temp.filter(p => {
                const cats = this.getDisplayCategories(p);
                return cats.includes(this.activeCategory);
            });
        }
        if (this.searchQuery && this.searchQuery.trim() !== '') {
            const query = this.searchQuery.toLowerCase().trim();
            temp = temp.filter(p => p.name.toLowerCase().includes(query) || (p.description && p.description.toLowerCase().includes(query)));
        }
        this.filteredPackages = temp;


        this.totalPages = Math.ceil(this.filteredPackages.length / this.itemsPerPage) || 1;
        this.currentPage = 1;
        this.updatePaginatedPackages();
    }

    updatePaginatedPackages() {
        const startIndex = (this.currentPage - 1) * this.itemsPerPage;
        const endIndex = startIndex + this.itemsPerPage;
        this.paginatedPackages = this.filteredPackages.slice(startIndex, endIndex);
    }

    nextPage() {
        if (this.currentPage < this.totalPages) {
            this.currentPage++;
            this.updatePaginatedPackages();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    prevPage() {
        if (this.currentPage > 1) {
            this.currentPage--;
            this.updatePaginatedPackages();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    goToPage(page: number) {
        if (page !== this.currentPage && page >= 1 && page <= this.totalPages) {
            this.currentPage = page;
            this.updatePaginatedPackages();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    getPageNumbers(): number[] {
        const pages: number[] = [];
        const maxVisible = 5;
        let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
        let end = start + maxVisible - 1;

        if (end > this.totalPages) {
            end = this.totalPages;
            start = Math.max(1, end - maxVisible + 1);
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    }

    getDisplayCategories(pkg: PackagesResponse): string[] {
        if (!pkg.categories || !pkg.categories.length) return ['Other'];
        return pkg.categories.map(c => this.categoryMap[c] || 'Other');
    }

    getDisplayCategory(pkg: PackagesResponse): string {
        return this.getDisplayCategories(pkg).join(', ');
    }

    getIcon(pkg: PackagesResponse): string {
        const cat = this.getDisplayCategories(pkg)[0] || 'Default';
        return this.packageIcons[cat] || this.packageIcons['Default'];
    }

    enquireNow() {
        this.router.navigate(['/contact-us']);
    }

    talkToExpert() {
        window.open('https://wa.me/919876996555', '_blank');
    }
    toggleFeatures(pkgId: number) {
        if (this.expandedFeatures.has(pkgId)) {
            this.expandedFeatures.delete(pkgId);
        } else {
            this.expandedFeatures.add(pkgId);
        }
    }

    isFeaturesExpanded(pkgId: number): boolean {
        return this.expandedFeatures.has(pkgId);
    }

    getFeatures(pkg: PackagesResponse): string[] {
        return pkg.servicesIncluded && pkg.servicesIncluded.length > 0
            ? pkg.servicesIncluded
            : ['Covers 5 dimensions including EQ', '56 expert-approved parameters', 'One free counselling session included'];
    }

    needsReadMore(pkg: PackagesResponse): boolean {
        const desc = pkg.description || "India's most comprehensive career psychometric evaluation";
        const textLength = desc.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length;
        const featuresCount = this.getFeatures(pkg).length;
        return textLength > 150 || featuresCount > 2;
    }
}
